import { useCallback, useEffect, useRef, useState } from 'react'

/** What the assistant panel knows about the threads it can talk in. */
export interface SidebarThreadList {
  /** The chat threads, newest first. */
  chatThreads: readonly { id: string }[]
  isLoading: boolean
  /** Creates a thread and answers it. */
  createThread: () => Promise<{ id: string }>
}

export type ThreadStep =
  /** A thread is selected. */
  | { kind: 'keep' }
  /** The list is still on its way. */
  | { kind: 'wait' }
  /** Nothing selected yet: take the latest chat thread. */
  | { kind: 'select'; threadId: string }
  /** There is no chat thread: the first one has to be made. */
  | { kind: 'create' }

/** What the panel does about its thread, given the selection and the list. */
export function threadStep(
  activeThreadId: string | undefined,
  list: Pick<SidebarThreadList, 'chatThreads' | 'isLoading'>
): ThreadStep {
  if (activeThreadId) return { kind: 'keep' }
  if (list.isLoading) return { kind: 'wait' }
  const latest = list.chatThreads[0]
  return latest ? { kind: 'select', threadId: latest.id } : { kind: 'create' }
}

/**
 * The thread the assistant panel talks in. The panel never offers a composer without one: a
 * message sent without a thread id makes the server start a new thread, so every message would
 * land in a thread of its own and the model would never see what was said before it.
 *
 * The latest chat thread is taken once the list is there. Where the user has none yet (a case
 * opened for the first time), the first one is made before anything can be typed; `failed` says
 * that it could not be made, and `retry` tries again.
 */
export function useSidebarThread(list: SidebarThreadList) {
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>(undefined)
  const [failed, setFailed] = useState(false)
  // One creation at a time, whatever re-renders (or StrictMode's second effect) come meanwhile.
  const creating = useRef(false)
  const createThread = useRef(list.createThread)
  createThread.current = list.createThread

  const startFirstThread = useCallback(async () => {
    if (creating.current) return
    creating.current = true
    setFailed(false)
    try {
      const thread = await createThread.current()
      // A thread asked for meanwhile (an "Open thread" request) stays the one shown.
      setActiveThreadId(current => current ?? thread.id)
    } catch (error) {
      console.error('[agent] could not start a conversation', error)
      setFailed(true)
    } finally {
      creating.current = false
    }
  }, [])

  const step = threadStep(activeThreadId, list)
  const selectId = step.kind === 'select' ? step.threadId : undefined
  useEffect(() => {
    if (selectId) setActiveThreadId(selectId)
    else if (step.kind === 'create' && !failed) void startFirstThread()
  }, [selectId, step.kind, failed, startFirstThread])

  return { activeThreadId, setActiveThreadId, failed, retry: startFirstThread }
}
